import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import AlertModal from 'components/AlertModal';
import { useAlert } from 'hooks';
import { Link } from 'react-router-dom';
import Header from 'components/Headers/Header';
import Loader from 'components/Loader';
import { ApiService } from 'services';
import { CreateData, OOSS, Response } from './types';
import FormClose from './FormClose';

const initialValues: CreateData = {
  remito_id: -1,
  ooss_id: -1,
};

export default function Close() {
  const [isLoading, setIsLoading] = useState(false);
  const [ooss, setOOSS] = useState<OOSS[]>([]);
  const [data, setData] = useState<CreateData>(initialValues);
  const [showAlert, message, alert] = useAlert(false, '');
  const [auditPending, setAuditPending] = useState<{
    bso_id: number,
    matricula: number,
    nombre: string,
  }[] | null>(null);

  useEffect(() => {
    getOOSS();
  }, []);

  const getOOSS = async () => {
    try {
      setIsLoading(true);
      const ooss = await ApiService.getAll('ooss', { presentsBill: true, limit: -1 }) as Response<OOSS>;
      setOOSS(ooss.rows);
    } catch (err) {
      console.error(err);
      alert(true, 'Ha ocurrido un error cargando las Obras Sociales');
    } finally {
      setIsLoading(false);
    }
  };

  const submit = async (force = false) => {
    try {
      setIsLoading(true);
      const body = force ? { force: true } : undefined;
      const res = await ApiService.post(`remitos/${ data.remito_id }/close`, body);

      if (res.error) {
        setIsLoading(false);
        alert(true, 'Ha ocurrido un error, por favor intente nuevamente');
        return;
      }

      if (res.status === 'audit_pending') {
        setAuditPending(res.pending);
        setIsLoading(false);
        return;
      }

      alert(true, `Se ha cerrado el remito ${ data.remito_id } con éxito:
        Solicitudes quitadas del remito: ${ res.data.solicitudes }
        Prestaciones borradas: ${ res.data.prestaciones }
        Pedidos de factura borrados: ${ res.data.facturas }`);

      getOOSS();
    } catch (err) {
      alert(true, 'Ha ocurrido un error, por favor intente nuevamente');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmAuditPending = () => {
    setAuditPending(null);
    submit(true);
  };

  const cancelAuditPending = () => {
    setAuditPending(null);
  };

  return (
    <>
      <AlertModal
        isOpen={showAlert}
        message={message}
        onClose={() => alert(false)}
      />
      <Header />
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0">
                  <span className='mr-2'>Cerrar pedido de presentación</span>
                  <Loader className={isLoading ? '': 'hidden'} />
                </h3>
              </Col>
              <Col className="text-right" xs="4">
                <Link to='/admin/pedidos-presentacion'>
                  <Button
                    color="primary"
                    size="md"
                  >
                    Volver
                  </Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <FormClose
              data={ data }
              setData={ setData }
              isLoading={ isLoading }
              ooss={ ooss }
              onSubmit={ () => submit(false) }
            />
          </CardBody>
        </Card>
      </Container>
      <Modal isOpen={auditPending !== null} toggle={cancelAuditPending}>
        <ModalHeader toggle={cancelAuditPending}>Auditoría pendiente</ModalHeader>
        <ModalBody>
          <p>Los siguientes BSOs fueron presentados por el matriculado pero todavía no fueron auditados:</p>
          <ul>
            {auditPending?.map(p => (
              <li key={p.bso_id}>{p.nombre} (Mat. {p.matricula}) - BSO #{p.bso_id}</li>
            ))}
          </ul>
          <p>
            Si continúas, estos matriculados serán <strong>excluidos del remito</strong> y sus prestaciones
            se moverán al próximo pedido. ¿Querés continuar de todas formas?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelAuditPending}>Cancelar</Button>
          <Button color="warning" onClick={confirmAuditPending}>Continuar de todas formas</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
